import { useCreateMemberMutation } from '#hooks/queries/member/useCreateMemberMutation';
import queryKeys from '#hooks/queries/queryKeys';
import { useToast } from '#providers/ToastProvider';
import { autoHyphen, removeHyphen } from '#utils/autoHyphen';
import { useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { ButtonSubmit } from 'src/components/pages/WeeklyCalenderScheduleRegistration/styles';

import { XButton } from '#assets/svg';
import TagManager from 'react-gtm-module';
import {
  Container,
  Header,
  Section,
  Required as StyledRequired,
  Text
} from 'src/shared/components/client-registration/styles';
import Select from '../select/Select';
import { Tab } from '../tab/Tab';
import { Tooltip } from '../tooltip/Tooltip';
import { ClientReigstrtaionInput } from './ClientRegistrationInput';

export const ClientRegistration = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { mutateAsync } = useCreateMemberMutation();

  const [name, setName] = useState<string>('');
  const [age, setAge] = useState<string | null>(null);
  const [sex, setSex] = useState<'MALE' | 'FEMALE' | null>(null);
  const [phoneNumber, setPhoneNumber] = useState<string>('');
  const [allowSelfScheduling, setAllowSelfScheduling] = useState<boolean>(true);

  const sexTab = { 남성: 'MALE', 여성: 'FEMALE' };
  const allowSelfSchedulingTab = { 네: true, 아니요: false };

  const ageOptions = Array.from({ length: 100 }, (_, i) => (i + 1).toString());

  const onPhoneNumberChange = (value: string) => {
    setPhoneNumber(autoHyphen(value));
  };

  const onNameChange = (value: string) => {
    setName(value);
  };

  const onAgeChange = (value: string) => {
    setAge(value);
  };

  const handleSubmit = async () => {
    try {
      if (!name) {
        showToast({
          type: 'fail',
          message: '성함을 입력해주세요.'
        });
        return;
      }

      if (!phoneNumber) {
        showToast({
          type: 'fail',
          message: '연락처를 입력해주세요.'
        });
        return;
      }

      if (allowSelfScheduling === null) {
        showToast({
          type: 'fail',
          message: '수업 예약 링크 전송 여부를 선택해주세요.'
        });
        return;
      }

      const payload = {
        name: name,
        phone: removeHyphen(phoneNumber),
        age: age ? parseInt(age) : null,
        sex: sex || null,
        allowSelfScheduling
      };
      const member = await mutateAsync(payload);
      TagManager.dataLayer({
        dataLayer: {
          event: 'memberdetail_memberadd_complete',
          category: '홈',
          action: 'memberdetail_memberadd_complete',
          value: 1,
          member_id: member
        }
      });
      showToast({
        type: 'success',
        message: '유저 등록에 성공 했습니다.'
      });
      queryClient.invalidateQueries([queryKeys.MEMBER]);
      navigate(-1);
    } catch (err) {
      let message = '유저 등록에 실패 했습니다.';

      if (isAxiosError(err) && err.response?.data.message) {
        message = err.response?.data.message as string;
      }

      showToast({
        type: 'fail',
        message
      });
    }
  };

  return (
    <Container>
      <Header>
        <XButton onClick={() => navigate('/manage', { state: 'account' })} />
        <Text>회원 등록</Text>
        <ButtonSubmit onClick={handleSubmit}>등록</ButtonSubmit>
      </Header>
      <Section>
        <div style={{ display: 'flex', gap: '2px' }}>
          <Text>성함</Text>
          <Required />
        </div>
        <ClientReigstrtaionInput onChange={onNameChange} />
      </Section>
      <Section>
        <Text>나이</Text>
        <Select
          selectContainerStyle={{ height: '53px' }}
          width={'100%'}
          placeholder="선택"
          defaultValue={'나이 선택'}
          options={ageOptions}
          onChange={age => onAgeChange(age)}
        />
      </Section>
      <Section>
        <Text>성별</Text>
        <Tab tab={sexTab} onClick={tab => setSex(tab)} />
      </Section>
      <Section>
        <div style={{ display: 'flex', gap: '2px' }}>
          <Text>연락처</Text>
          <Required />
        </div>
        <ClientReigstrtaionInput
          placeholder="-를 제외한 번호를 입력해주세요"
          onChange={onPhoneNumberChange}
        />
      </Section>
      <Section>
        <div style={{ display: 'flex', gap: '4px' }}>
          <div style={{ display: 'flex', gap: '2px' }}>
            <Text>수업 예약 링크 전송 여부</Text>
            <Required />
          </div>
          <Tooltip
            tooltipTrigger={
              <img
                src="/images/help-24-dp-5-f-6368-fill-1-wght-400-grad-0-opsz-241.svg"
                width={'16px'}
                height={'16px'}
              />
            }
            position="top-center"
          >
            <div style={{ width: '260px' }}>
              <div
                style={{
                  color: 'var(--gray-15---main-font, #EAE8ED)',
                  fontFamily: '"Pretendard Variable"',
                  fontSize: '12px',
                  fontStyle: 'normal',
                  fontWeight: 500,
                  lineHeight: '16px',
                  textAlign: 'left'
                }}
              >
                수업 예약 링크란?
              </div>
              <div
                style={{
                  color: 'var(--gray-100---sub-font, #8E899F)',
                  fontFamily: '"Pretendard Variable"',
                  fontSize: '12px',
                  fontStyle: 'normal',
                  fontWeight: 400,
                  lineHeight: '16px',
                  textAlign: 'left'
                }}
              >
                회원이 직접 수업을 예약할 수 있도록 카카오톡 알림톡으로 예약
                링크를 전송해드려요! (회원을 등록하시면 즉시 전송됩니다)
              </div>
            </div>
          </Tooltip>
        </div>
        <Tab
          defaultTab="네"
          tab={allowSelfSchedulingTab}
          onClick={tab => setAllowSelfScheduling(tab)}
        />
      </Section>
    </Container>
  );
};

const Required = () => {
  return <StyledRequired>*</StyledRequired>;
};
